import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  useInfiniteQuery,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import { toast } from "react-toastify";
import { FaSearch, FaPlus, FaTicketAlt } from "react-icons/fa";
import {
  getPublishedModes,
  addModeByInvitation,
} from "../../api/custom-mode";
import PublishedModeCard from "../../components/cards/PublishedModeCard";
import { queueEvent } from "../../analytics";
import { EVENTS } from "../../analytics/events";
import { Button, Input, Modal, Spinner } from "../../ui";

const SORTS = [
  { value: "newest", label: "الأحدث" },
  { value: "mostLikes", label: "الأكثر إعجاباً" },
  { value: "mostPlayed", label: "الأكثر لعباً" },
  { value: "mostQuestions", label: "الأكثر أسئلة" },
];

const iconButton =
  "inline-flex items-center justify-center w-9 h-9 rounded-lg border border-white/15 bg-white/10 text-white/85 hover:text-white hover:bg-white/15 transition-colors";

const Grid = ({ modes }) => (
  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3 sm:gap-4">
    {modes.map((m) => (
      <PublishedModeCard key={m._id} mode={m} />
    ))}
  </div>
);

// The community page built on the previous endpoint
// (/api/custom-mode/published-modes). Shown when the server doesn't have
// /api/community yet.
const CommunityLegacy = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [sort, setSort] = useState("newest");
  const [codeOpen, setCodeOpen] = useState(false);
  const [code, setCode] = useState("");

  const { data, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useInfiniteQuery({
      queryKey: ["publishedModes", sort],
      queryFn: ({ pageParam = 1 }) => getPublishedModes(pageParam, 12, sort),
      initialPageParam: 1,
      getNextPageParam: (last, all) =>
        (last?.recentModes?.length || 0) >= 12 ? all.length + 1 : undefined,
    });

  const page1 = data?.pages?.[0];
  const featured = page1?.featuredModes || [];
  const forYou = page1?.forYouModes || [];
  const recent = (data?.pages || []).flatMap((p) => p?.recentModes || []);

  const addByCode = useMutation({
    mutationFn: () => addModeByInvitation(code.trim()),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["myCustomModes"] });
      queueEvent(EVENTS.MODE_ADDED_BY_CODE);
      setCode("");
      setCodeOpen(false);
      toast.success("تمت إضافة الفئة");
    },
    onError: (e) => toast.error(e?.response?.data?.message || "كود غير صحيح"),
  });

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <h1 className="text-2xl md:text-3xl font-extrabold">فئات المجتمع</h1>
        <div className="flex items-center gap-2">
          <Button size="sm" icon={<FaPlus />} onClick={() => navigate("/app/modes/create")}>
            إنشاء فئة
          </Button>
          <button
            onClick={() => setCodeOpen(true)}
            className={iconButton}
            aria-label="إضافة بكود"
            title="إضافة بكود"
          >
            <FaTicketAlt />
          </button>
          <button
            onClick={() => navigate("/app/community/search")}
            className={iconButton}
            aria-label="بحث"
            title="بحث"
          >
            <FaSearch />
          </button>
        </div>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-16 text-white/80">
          <Spinner size={28} />
        </div>
      ) : (
        <>
          {featured.length > 0 && (
            <section className="mb-8">
              <h2 className="text-lg font-bold mb-3">ذات شعبية</h2>
              <Grid modes={featured} />
            </section>
          )}
          {forYou.length > 0 && (
            <section className="mb-8">
              <h2 className="text-lg font-bold mb-3">على ذوقك</h2>
              <Grid modes={forYou} />
            </section>
          )}

          <div className="flex flex-wrap gap-2 mb-5">
            {SORTS.map((s) => (
              <button
                key={s.value}
                onClick={() => setSort(s.value)}
                aria-pressed={sort === s.value}
                className={`h-9 px-3.5 rounded-full border text-sm font-semibold transition-colors ${
                  sort === s.value
                    ? "bg-mainButton border-mainButton text-white"
                    : "bg-white/[0.05] border-white/10 text-white/70 hover:text-white hover:border-white/25"
                }`}
              >
                {s.label}
              </button>
            ))}
          </div>

          {recent.length === 0 ? (
            <p className="text-white/55 text-center py-10">لا توجد فئات</p>
          ) : (
            <>
              <Grid modes={recent} />
              {hasNextPage && (
                <div className="flex justify-center mt-8">
                  <Button
                    variant="secondary"
                    onClick={() => fetchNextPage()}
                    disabled={isFetchingNextPage}
                    icon={isFetchingNextPage ? <Spinner /> : null}
                  >
                    عرض المزيد
                  </Button>
                </div>
              )}
            </>
          )}
        </>
      )}

      <Modal
        open={codeOpen}
        onClose={() => setCodeOpen(false)}
        title="إضافة فئة بكود الدعوة"
        size="sm"
      >
        <Input
          type="text"
          ltr
          value={code}
          onChange={(e) => setCode(e.target.value)}
          placeholder="كود الدعوة"
          aria-label="كود الدعوة"
          className="text-right mb-5"
        />
        <div className="flex gap-3">
          <Button
            className="flex-1"
            onClick={() => code.trim() && addByCode.mutate()}
            disabled={addByCode.isPending}
            icon={addByCode.isPending ? <Spinner /> : null}
          >
            إضافة
          </Button>
          <Button variant="secondary" className="flex-1" onClick={() => setCodeOpen(false)}>
            إلغاء
          </Button>
        </div>
      </Modal>
    </div>
  );
};

export default CommunityLegacy;
